import { useMemo } from 'react';

import { type PersonalInfoConfig, type PersonalInfo } from '../models/repair-bookings';
import {
    validateEmail,
    validateKana,
    validateMobilePhoneNumber,
    validatePhoneNumber,
} from '../utils/validators';

const usePersonalInfo = (info: PersonalInfo, config: PersonalInfoConfig) => {
    const defaultLineStatus =
        config.granularity === 'extended' ? (config.lineStatuses?.[0]?.key ?? '') : '';
    const hasValidLineStatus =
        config.granularity === 'extended' &&
        config.lineStatuses?.some((status) => status.key === info.line_status);
    const selectedLineStatus = hasValidLineStatus ? (info.line_status ?? '') : defaultLineStatus;

    const filled = useMemo(() => {
        const required =
            config.granularity === 'extended'
                ? [
                      info.last_name,
                      info.last_name_pronunciation,
                      info.birthday,
                      info.phone_number,
                      info.line_status,
                      info.subscriber_code,
                  ]
                : [info.last_name, info.last_name_pronunciation, info.phone_number, info.email];
        return required.every((value) => value && value.trim().length > 0);
    }, [info, config]);

    const errorMessage = useMemo(() => {
        return validatePersonalInfoFields(info);
    }, [info]);

    return { filled, errorMessage, defaultLineStatus, selectedLineStatus };
};

const validatePersonalInfoFields = (info: PersonalInfo): string | null => {
    const validations: Array<{
        value: string;
        validator: (value: string) => boolean;
        errorMessage: string;
    }> = [
        {
            value: info.last_name_pronunciation,
            validator: validateKana,
            errorMessage: '姓（カナ）は全角カタカナで入力してください。',
        },
        {
            value: info.first_name_pronunciation,
            validator: validateKana,
            errorMessage: '名（カナ）は全角カタカナで入力してください。',
        },
        {
            value: info.phone_number,
            validator: validateMobilePhoneNumber,
            errorMessage: '携帯電話番号は半角数字11桁（ハイフン使用可）で入力してください。',
        },
        {
            value: info.alt_phone_number,
            validator: validatePhoneNumber,
            errorMessage: '連絡先電話番号は半角数字10〜11桁（ハイフン使用可）で入力してください。',
        },
        {
            value: info.email,
            validator: validateEmail,
            errorMessage: '電子メールの形式が正しくありません。',
        },
    ];

    for (const { value, validator, errorMessage } of validations) {
        if (value.trim().length === 0) {
            continue;
        }
        if (!validator(value)) {
            return errorMessage;
        }
    }
    return null;
};

export default usePersonalInfo;
